import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { FormControl, Autocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import { usePlaylistsQuery } from 'pages/knowledge/hooks/useArticlesQuery';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';

type PlaylistOptionType = { label: string; value: string };

type PostPlaylistsProps = {
  field: ConfigEntryType;
};

function PostPlaylists({ field }: PostPlaylistsProps) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<PostFormData>();
  const selectedPlaylists = (useWatch({ control })?.playlists || []) as PlaylistOptionType[];
  const { playlists, isLoading: playlistsLoading } = usePlaylistsQuery();
  const playlistsOptions = useMemo(
    () => playlists.map((playlist) => ({ label: playlist.title, value: playlist.id })),
    [playlists],
  );
  const { pluralCollectionsModuleName } = useBuildWiseModules();

  const handleSelectPlaylists = (_: unknown, playlistIds: PlaylistOptionType[]) => {
    setValue('playlists', playlistIds, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState }) => (
          <FormControl fullWidth>
            <Autocomplete
              multiple
              limitTags={3}
              disableCloseOnSelect
              onChange={handleSelectPlaylists}
              options={playlistsOptions}
              fullWidth
              autoHighlight
              value={selectedPlaylists || []}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              noOptionsText={
                playlistsLoading ? 'Loading...' : `No ${pluralCollectionsModuleName} Found`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Add to ${pluralCollectionsModuleName}`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {playlistsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  error={!!fieldState.error}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    label={option.label}
                    deleteIcon={<CloseLineIcon size={20} />}
                  />
                ))
              }
              disabled={isSubmitting}
            />
          </FormControl>
        )}
        name={field?.entryCode}
      />
    </>
  );
}

export default PostPlaylists;
